import React, { useCallback } from 'react';

import {
  Box,
  Button,
  FormControl,
  GridLegacy as Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';

import InputDeparturePortSelect from '~/components/Cruises/components/Selects/InputDeparturePortSelect';
import { Port } from '~/components/Cruises/pages/Ports/types';

import { DepartureSearchState } from './DepartureGridConfig';

interface Props {
  searchFields: DepartureSearchState;
  onSearchFieldsChange: React.Dispatch<React.SetStateAction<DepartureSearchState>>;
  onSearch: () => void;
  isSearching: boolean;
  onKeyDown: (e: React.KeyboardEvent) => void;
  vendorName: string | undefined;
  selectedPorts: Array<Port>;
  onPortsChange: React.Dispatch<React.SetStateAction<Array<Port>>>;
}

export default function DepartureSearchForm({
  searchFields,
  onSearchFieldsChange,
  onSearch,
  isSearching,
  onKeyDown,
  vendorName,
  selectedPorts,
  onPortsChange,
}: Props) {
  const handleFieldChange = useCallback(
    (field: keyof DepartureSearchState) => (e: React.ChangeEvent<HTMLInputElement>) => {
      onSearchFieldsChange((prev) => ({ ...prev, [field]: e.target.value }));
    },
    [onSearchFieldsChange],
  );

  return (
    <Box sx={{ pt: 1 }}>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            label="Cruise Line"
            size="small"
            value={vendorName ?? ''}
            InputProps={{ readOnly: true }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            label="Sailing Name"
            size="small"
            value={searchFields.name}
            onChange={handleFieldChange('name')}
            onKeyDown={onKeyDown}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            label="Ship Name"
            size="small"
            value={searchFields.shipName}
            onChange={handleFieldChange('shipName')}
            onKeyDown={onKeyDown}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={searchFields.status}
              onChange={(e) => onSearchFieldsChange((prev) => ({ ...prev, status: e.target.value }))}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="INACTIVE">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            label="Offer ID"
            size="small"
            value={searchFields.offerId}
            onChange={handleFieldChange('offerId')}
            onKeyDown={onKeyDown}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            label="Sailing or Tour External ID"
            size="small"
            type="number"
            value={searchFields.sailingExternalId}
            onChange={handleFieldChange('sailingExternalId')}
            onKeyDown={onKeyDown}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            label="Ship External ID"
            size="small"
            type="number"
            value={searchFields.shipExternalId}
            onChange={handleFieldChange('shipExternalId')}
            onKeyDown={onKeyDown}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            label="Sailing Duration Min (days)"
            size="small"
            type="number"
            value={searchFields.sailingDurationMin}
            onChange={handleFieldChange('sailingDurationMin')}
            onKeyDown={onKeyDown}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            label="Sailing Duration Max (days)"
            size="small"
            type="number"
            value={searchFields.sailingDurationMax}
            onChange={handleFieldChange('sailingDurationMax')}
            onKeyDown={onKeyDown}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            label="Departure Date From"
            size="small"
            type="date"
            value={searchFields.departureDateStart}
            onChange={handleFieldChange('departureDateStart')}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            label="Departure Date To"
            size="small"
            type="date"
            value={searchFields.departureDateEnd}
            onChange={handleFieldChange('departureDateEnd')}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12}>
          <InputDeparturePortSelect
            selectedValues={selectedPorts}
            setSelectedValues={onPortsChange}
            departurePorts={[]}
          />
        </Grid>
      </Grid>

      <Stack direction="row" sx={{ mb: 2 }}>
        <Button variant="contained" onClick={onSearch} disabled={isSearching}>
          Search
        </Button>
      </Stack>
    </Box>
  );
}
